import { type Block, type Plan, type Reference, groupsPhrase, isWorking, newID, planFromWire, planWire } from "./models.js";
import { type DefinitionReading, type Pick, expandLines, isCurrentReading, picksFromLogs, picksIn, publishedBlocks, readDefinition } from "./reading.js";
import { renderPlan } from "./planText.js";
import { type LineRecord, blocksWithRecords, formPicks, formRows, lineRecords, logRecords } from "./rows/records.js";
import type { Row } from "./rows/types.js";
import { ROWS_VERSION } from "./rows/valid.js";
import { type ParseResult, parse } from "./ruleParser.js";
import { addDays, dateFromDay, dayString, mondayOf, parts, startOfDay } from "./time.js";
import { chars, count, isNumber, splitSpaces, trimWS, trimWSNL } from "./text.js";
import {
  type LineOrigin,
  type PlannedDay,
  type TrainingDay,
  type TrainingWeek,
  type WeightsSession,
  daySeed,
  displayLabel,
  foldText,
  identifierOn,
  isEveryone,
  isPrepLabel,
  labelKey,
  looksLikeLabel,
  matchesGroup,
  othersAround,
  trainingDays,
  weightsSessions,
} from "./trainingWeek.js";
import type {
  BlockWire,
  PlanColumn,
  PlanDay,
  PlanDefinition,
  PlanDraft,
  LineReading,
  PublishBody,
  PublishedRow,
  PublishedWeek,
  ReferenceSessionWire,
  WeightsReading,
  WorkoutPlanWire,
} from "./types.js";

export interface ReadOptions {
  now?: Date;
  knownExercises?: string[];
  /** The admin's phone's zone: a day's start in it is part of every session's id. */
  timeZone?: string;
}

export const defaultTimeZone = "America/Toronto";

// MARK: The draft as a week (DESIGN.md §17.3)

const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function rowLabel(ms: number, timeZone: string): string {
  const p = parts(ms, timeZone);
  return `${weekdayNames[p.weekday - 1]} ${p.day} ${monthNames[p.month - 1]}`;
}

export function toTrainingWeek(draft: PlanDraft, options: { timeZone?: string } = {}): TrainingWeek {
  const timeZone = options.timeZone ?? defaultTimeZone;
  const days: TrainingDay[] = draft.days.map((day) => {
    const date = dateFromDay(day.date, timeZone);
    const sessions = new Map<string, string[]>();
    for (const column of draft.columns) {
      const entries = (day.cells[column.id] ?? []).map(trimWSNL).filter((entry) => entry !== "");
      if (entries.length > 0) sessions.set(column.name, [...(sessions.get(column.name) ?? []), ...entries]);
    }
    const location = trimWSNL(day.location);
    return {
      date,
      label: date !== null ? rowLabel(date, timeZone) : day.date,
      location: location === "" ? null : location,
      sessions,
    };
  });
  const glossary = new Map<string, string[]>();
  const records = new Map<string, (LineRecord | null)[]>();
  for (const definition of draft.sessions) {
    const label = trimWSNL(definition.label);
    if (label === "") continue;
    glossary.set(label, [...(glossary.get(label) ?? []), ...definition.lines]);
    records.set(label, [...(records.get(label) ?? []), ...definitionRecords(definition)]);
  }
  const title = trimWSNL(draft.title);
  return { title: title === "" ? null : title, groups: draft.columns.map((c) => c.name), days, glossary, records };
}

/** Per line, what it logs: from `logs` once the coach set any (§32.6), else from the rows of a form definition untouched since (§25.6). */
function definitionRecords(definition: PlanDefinition): (LineRecord | null)[] {
  if (definition.logs !== undefined) return logRecords(definition.lines, definition.logs);
  const rows = formRows(definition);
  return rows === null ? definition.lines.map(() => null) : lineRecords(rows);
}

// MARK: PublishedWeeks

/** How a publisher reads a sheet: each personal column with everyone's, then everyone's alone. */
export function readingsOf(groups: readonly string[]): { group: string; columns: string[] }[] {
  const everyone = groups.filter(isEveryone);
  const personal = groups.filter((g) => !isEveryone(g));
  const readings = personal.map((column) => ({
    group: column,
    columns: groups.filter((g) => everyone.includes(g) || g === column),
  }));
  if (everyone.length > 0) readings.push({ group: "", columns: everyone });
  return readings;
}

/** `PublishedWeeks.column(in:for:)`: the athlete's names in their own order; never everyone's. */
export function columnFor(columns: readonly string[], myGroups: readonly string[]): string | null {
  const personal = columns.filter((c) => !isEveryone(c));
  for (const name of myGroups) {
    const match = personal.find((c) => matchesGroup(c, name));
    if (match !== undefined) return match;
  }
  return null;
}

/**
 * The §15 `mine` rule for this draft: the row group an athlete with these My columns is given,
 * the draft's columns that reading shows them, and whether the phone would ask which is theirs.
 */
export function columnsMatching(
  draft: PlanDraft,
  athleteGroups: readonly string[],
): { group: string; columns: string[]; unmatched: boolean } {
  const names = draft.columns.map((c) => c.name);
  const chosen = columnFor(names, athleteGroups);
  const personal = names.filter((n) => !isEveryone(n));
  const everyone = names.filter(isEveryone);
  if (chosen === null) return { group: "", columns: everyone, unmatched: personal.length > 0 };
  return { group: chosen, columns: names.filter((n) => n === chosen || everyone.includes(n)), unmatched: false };
}

// MARK: The admin's phone, rules only

class Model {
  constructor(
    public plan: Plan,
    public alsoToday: Reference[],
    public reading: ParseResult | null,
    public label: string,
    public lines: LineReading[] = [],
  ) {}

  get date(): number {
    return this.plan.date ?? this.plan.createdAt;
  }

  get cleanedBlocks(): Block[] {
    const kept: Block[] = [];
    for (const block of this.plan.blocks) {
      const exercises = block.exercises.flatMap((e) => {
        const name = trimWSNL(e.name);
        if (name === "") return [];
        return [{ ...e, name, note: trimmedOrNull(e.note) }];
      });
      if (exercises.length === 0) continue;
      kept.push({ ...block, note: trimmedOrNull(block.note), sets: Math.max(block.sets, 1), exercises });
    }
    return [...kept.filter((b) => b.isPrep), ...kept.filter(isWorking)];
  }

  get canSave(): boolean {
    return this.cleanedBlocks.some(isWorking) || this.alsoToday.length > 0;
  }

  saved(teamSessionID: string, alsoToday: Reference[]): Plan {
    return {
      id: this.plan.id,
      title: trimWSNL(this.plan.title),
      date: this.date,
      createdAt: this.plan.createdAt,
      sourceText: this.plan.sourceText,
      interpretedBy: this.plan.interpretedBy,
      blocks: this.cleanedBlocks,
      alsoToday,
      teamSessionID,
      location: this.plan.location ?? null,
    };
  }
}

function trimmedOrNull(text: string | null): string | null {
  if (text === null) return null;
  const cleaned = trimWSNL(text);
  return cleaned === "" ? null : cleaned;
}

interface Interpreted {
  plan: Plan;
  result: ParseResult;
  lines: LineReading[];
}

/**
 * The expanded text's line readings as the definition's own lines: a line naming another
 * definition (`Weights Prep #1`) is a block, carrying the first block its lines made.
 */
export function ownLineReadings(lines: LineReading[], ownLines: string[], origins: LineOrigin[]): LineReading[] {
  const own = new Map<number, LineReading>();
  const via = new Map<number, string | undefined>();
  lines.forEach((reading, index) => {
    const origin = origins[index] ?? null;
    if (origin === null) return;
    if ("own" in origin) {
      if (!own.has(origin.own)) own.set(origin.own, reading);
    } else if (via.get(origin.via) === undefined) {
      via.set(origin.via, reading.blockId);
    }
  });
  return ownLines.map((text, index) => {
    const found = own.get(index);
    if (found !== undefined) return { ...found, index, text, exerciseIds: found.exerciseIds.slice() };
    const reading: LineReading = { index, text, kind: "block", exerciseIds: [] };
    const blockId = via.get(index);
    if (blockId !== undefined) reading.blockId = blockId;
    return reading;
  });
}

function interpret(sessions: WeightsSession[], known: string[], now: number, timeZone: string): Map<string, Interpreted> {
  const out = new Map<string, Interpreted>();
  let previous: number | null = null;
  for (const session of sessions) {
    const result = parse(session.text, { knownExercises: known, now, timeZone });
    const date: number = session.date ?? (previous !== null ? addDays(previous, 1, timeZone) : startOfDay(now, timeZone));
    previous = date;
    const plan: Plan = { ...result.plan, blocks: blocksWithRecords(result.plan.blocks, result.lines, session.records), date, sourceText: session.text, title: "" };
    if (trimWSNL(session.text) === "") plan.blocks = [];
    out.set(session.id, { plan, result, lines: ownLineReadings(result.lines, session.ownLines, session.origins) });
  }
  return out;
}

export interface ColumnSession {
  draftID: string;
  label: string;
  plan: Plan;
  reading: ParseResult | null;
  lines: LineReading[];
}

/** Every column of the week as an athlete in it would read it, in the order the review saves them. */
export function readColumns(week: TrainingWeek, options: ReadOptions = {}): { group: string; sessions: ColumnSession[] }[] {
  const timeZone = options.timeZone ?? defaultTimeZone;
  const now = (options.now ?? new Date()).getTime();
  const known = options.knownExercises ?? [];
  const readings = readingsOf(week.groups);

  const sessions: WeightsSession[] = [];
  for (const reading of readings) {
    for (const session of weightsSessions(week, reading.columns)) {
      if (!sessions.some((s) => s.id === session.id)) sessions.push(session);
    }
  }
  const interpreted = interpret(sessions, known, now, timeZone);

  const shared = new Map<string, Model>();
  const out: { group: string; sessions: ColumnSession[] }[] = [];
  for (const reading of readings) {
    const ids = new Set(weightsSessions(week, reading.columns).map((s) => s.id));
    const days: PlannedDay[] = trainingDays(week, reading.columns);
    const others = new Map<string, Reference[]>();
    const order = new Map<string, number>();
    const locations = new Map<string, string | null>();
    let next = 0;
    for (const day of days) {
      locations.set(day.id, day.location);
      for (const weights of day.weights) locations.set(weights.id, day.location);
      if (day.weights.length === 0) {
        order.set(day.id, next);
        next += 1;
        continue;
      }
      for (const weights of day.weights) {
        others.set(weights.id, othersAround(day, weights));
        order.set(weights.id, next);
        next += 1;
      }
    }

    interface Draft {
      id: string;
      model: Model;
      order: number;
      sheetAlsoToday: Reference[];
    }
    const drafts: Draft[] = [];
    sessions.forEach((session, index) => {
      if (!ids.has(session.id)) return;
      const read = interpreted.get(session.id)!;
      const model =
        shared.get(session.id) ??
        new Model({ ...read.plan, location: locations.get(session.id) ?? null }, others.get(session.id) ?? [], read.result, session.label, read.lines);
      drafts.push({ id: session.id, model, order: order.get(session.id) ?? next + index, sheetAlsoToday: others.get(session.id) ?? [] });
    });
    for (const day of days) {
      if (day.weights.length > 0 || day.others.length === 0) continue;
      const plan: Plan = {
        id: newID(),
        title: "",
        date: day.date,
        createdAt: now,
        sourceText: null,
        interpretedBy: null,
        blocks: [],
        alsoToday: day.others,
        teamSessionID: null,
        location: day.location,
      };
      const model = shared.get(day.id) ?? new Model(plan, day.others, null, "");
      drafts.push({ id: day.id, model, order: order.get(day.id) ?? next, sheetAlsoToday: day.others });
    }
    drafts.sort((a, b) => {
      const x = startOfDay(a.model.date, timeZone);
      const y = startOfDay(b.model.date, timeZone);
      if (x !== y) return x - y;
      return a.order - b.order;
    });
    for (const draft of drafts) if (!shared.has(draft.id)) shared.set(draft.id, draft.model);
    const isFromSheet = days.length > 0;
    out.push({
      group: reading.group,
      sessions: drafts
        .filter((d) => d.model.canSave)
        .map((d) => ({
          draftID: d.id,
          label: d.model.label,
          plan: d.model.saved(d.id, isFromSheet ? d.sheetAlsoToday : d.model.alsoToday),
          reading: d.model.reading,
          lines: d.model.lines,
        })),
    });
  }
  return out;
}

/** DESIGN §25.6: the rows of the definition a session and each of its other parts were read from, when the form wrote them. */
function withRows(plan: WorkoutPlanWire, label: string, rows: Map<string, Row[]>): WorkoutPlanWire {
  const own = rows.get(labelKey(label));
  const alsoToday = plan.alsoToday.map((part) => {
    const theirs = rows.get(labelKey(part.label));
    return theirs === undefined ? part : { ...part, rows: theirs, rowsVersion: ROWS_VERSION };
  });
  return own === undefined ? { ...plan, alsoToday } : { ...plan, alsoToday, rows: own, rowsVersion: ROWS_VERSION };
}

/** By label key, the rows of each definition the form wrote and nothing has set `logs` on since; a label defined twice reads as both, which no rows say. */
function rowsByLabel(draft: PlanDraft): Map<string, Row[]> {
  const out = new Map<string, Row[]>();
  const seen = new Set<string>();
  for (const definition of draft.sessions) {
    const key = labelKey(definition.label);
    if (key === "") continue;
    if (seen.has(key)) {
      out.delete(key);
      continue;
    }
    seen.add(key);
    const rows = definition.logs === undefined ? formRows(definition) : null;
    if (rows !== null) out.set(key, rows);
  }
  return out;
}

// MARK: Publishing from the readings (DESIGN.md §61.4)

export interface LabelReading {
  label: string;
  reading: DefinitionReading;
  /** The lines it was read from, references expanded. */
  lines: string[];
}

/** A definition's own picks: those its current reading holds, else those a coach app before §61 sent. */
export function ownPicks(definition: PlanDefinition, definitions: readonly PlanDefinition[]): Pick[] {
  if (isCurrentReading(definition.reading)) return picksIn(definition.reading, expandLines(definition.label, definition.lines, definitions).lines);
  return legacyPicks(definition);
}

/** The picks of the definitions this one names (a prep it includes): they hold for those lines here too. */
export function inheritedPicks(
  definition: { label: string; lines: readonly string[] },
  definitions: readonly PlanDefinition[],
  picksOfDefinition: (definition: PlanDefinition) => Pick[] = (d) => ownPicks(d, definitions),
): Pick[] {
  const own = labelKey(definition.label);
  const named = new Set(definition.lines.map(labelKey).filter((key) => key !== "" && key !== own));
  return definitions.filter((d) => named.has(labelKey(d.label))).flatMap(picksOfDefinition);
}

/** The draft with every definition holding a current reading: kept when it has one, else read, keeping its picks. */
export function withReadings(draft: PlanDraft, options: ReadOptions = {}): PlanDraft {
  const definitions = draft.sessions.map((d) => ({ label: d.label, lines: d.lines }));
  let changed = false;
  const sessions = draft.sessions.map((definition) => {
    if (isCurrentReading(definition.reading)) return definition;
    changed = true;
    const reading = readDefinition(definition.label, definition.lines, definition.reading ?? null, {
      definitions,
      priorLines: definition.lines,
      now: options.now,
      timeZone: options.timeZone,
      knownExercises: options.knownExercises,
      picks: legacyPicks(definition),
      inherited: inheritedPicks(definition, draft.sessions),
    });
    const { logs: _logs, ...rest } = definition;
    return { ...rest, reading };
  });
  return changed ? { ...draft, sessions } : draft;
}

/** What a definition read before §61 says it logs: a coach app's `logs`, else the form's records when it has no reading yet. */
export function legacyPicks(definition: PlanDefinition): Pick[] {
  if (definition.logs !== undefined) return picksFromLogs(definition.logs);
  return definition.reading === undefined ? formPicks(definition) : [];
}

/** By label key, each definition's reading; a label defined twice is read as both, one after the other. */
export function readingsByLabel(draft: PlanDraft, options: ReadOptions = {}): Map<string, LabelReading> {
  const definitions = draft.sessions.map((d) => ({ label: d.label, lines: d.lines }));
  const grouped = new Map<string, PlanDefinition[]>();
  for (const definition of draft.sessions) {
    const key = labelKey(definition.label);
    if (key === "") continue;
    grouped.set(key, [...(grouped.get(key) ?? []), definition]);
  }
  const out = new Map<string, LabelReading>();
  for (const [key, list] of grouped) {
    const label = list[0]!.label;
    const lines = list.flatMap((d) => d.lines);
    const reading =
      list.length === 1 && isCurrentReading(list[0]!.reading)
        ? list[0]!.reading
        : readDefinition(label, lines, list.length === 1 ? (list[0]!.reading ?? null) : null, {
            definitions,
            priorLines: lines,
            now: options.now,
            timeZone: options.timeZone,
            knownExercises: options.knownExercises,
            picks: list.length === 1 ? legacyPicks(list[0]!) : [],
            inherited: inheritedPicks({ label, lines }, draft.sessions),
          });
    out.set(key, { label, reading, lines: expandLines(label, lines, definitions).lines });
  }
  return out;
}

function referenceWire(reference: Reference, readings: Map<string, LabelReading>): ReferenceSessionWire {
  const wire: ReferenceSessionWire = { id: reference.id, label: reference.label, lines: reference.lines.slice(), isBeforeLifts: reference.isBeforeLifts };
  if (readings.get(labelKey(reference.label))?.reading.family === "run") wire.isRun = true;
  return wire;
}

/**
 * The body of `PUT /team/weeks/:monday`: every column's sessions from the definitions' readings
 * (§61.4). A weights session's blocks come first in its part of the day; the day's other parts ride,
 * as blocks with `loggedFrom`, on the day's first weights session only, or on a day row of their own.
 * Ids come from the day's seed, so the same week published twice is the same rows.
 */
export function publishRows(draft: PlanDraft, options: ReadOptions = {}): PublishBody {
  const timeZone = options.timeZone ?? defaultTimeZone;
  const now = (options.now ?? new Date()).getTime();
  const read = withReadings(draft, options);
  const readings = readingsByLabel(read, options);
  const week = toTrainingWeek(read, { timeZone });
  const rows = rowsByLabel(read);
  const planIDs = new Map<string, string>();
  const planID = (sessionID: string): string => {
    let id = planIDs.get(sessionID);
    if (id === undefined) {
      id = newID();
      planIDs.set(sessionID, id);
    }
    return id;
  };
  const sessions: PublishedRow[] = [];
  for (const column of readingsOf(week.groups)) {
    const made: { date: number; row: PublishedRow }[] = [];
    for (const day of trainingDays(week, column.columns)) {
      const seed = daySeed({ date: day.date, label: day.label, location: day.location, sessions: new Map() });
      const date = day.date ?? startOfDay(now, timeZone);
      const position = (label: string) => day.positions.get(labelKey(label)) ?? Number.MAX_SAFE_INTEGER;
      const blocksOf = (label: string, loggedFrom?: string): BlockWire[] => {
        const found = readings.get(labelKey(label));
        if (found === undefined) return [];
        return publishedBlocks(found.reading, { label, seed, lines: found.lines, ...(loggedFrom === undefined ? {} : { loggedFrom }) });
      };
      const place = (sessionID: string, label: string, plan: Plan, blocks: BlockWire[], alsoToday: Reference[]) => {
        const wire = planWire(plan);
        wire.blocks = blocks;
        wire.alsoToday = alsoToday.map((r) => referenceWire(r, readings));
        made.push({
          date: startOfDay(date, timeZone),
          row: { group: column.group, sessionID, date: dayString(date, timeZone), plan: withRows(wire, label, rows) },
        });
      };
      if (day.weights.length === 0) {
        if (day.others.length === 0) continue;
        const blocks = day.others.flatMap((other) => blocksOf(other.label, other.label));
        const plan: Plan = {
          id: planID(day.id),
          title: "",
          date,
          createdAt: now,
          sourceText: null,
          interpretedBy: null,
          blocks: [],
          alsoToday: [],
          teamSessionID: day.id,
          location: day.location,
        };
        place(day.id, "", plan, blocks, day.others);
        continue;
      }
      const ordered = day.weights.slice().sort((a, b) => position(a.label) - position(b.label));
      ordered.forEach((weights, n) => {
        const own = blocksOf(weights.label);
        const others = n === 0 ? othersAround(day, weights) : [];
        if (!own.some((b) => !b.isPrep) && others.length === 0) return;
        const parts = others.map((other) => ({ at: position(other.label), blocks: blocksOf(other.label, other.label) }));
        const blocks = [{ at: position(weights.label), blocks: own }, ...parts]
          .map((entry, index) => ({ ...entry, index }))
          .sort((a, b) => a.at - b.at || a.index - b.index)
          .flatMap((entry) => entry.blocks);
        const found = readings.get(labelKey(weights.label));
        const plan: Plan = {
          id: planID(weights.id),
          title: trimWSNL(weights.label),
          date,
          createdAt: now,
          sourceText: found === undefined ? weights.text : found.lines.join("\n"),
          interpretedBy: "rules",
          blocks: [],
          alsoToday: [],
          teamSessionID: weights.id,
          location: day.location,
        };
        place(weights.id, weights.label, plan, blocks, others);
      });
    }
    made.sort((a, b) => a.date - b.date);
    sessions.push(...made.map((m) => m.row));
  }
  return { columns: week.groups, sessions };
}

/**
 * §61.4's backfill for a week with no draft to read: its rows as published, each day's other parts
 * read from the lines they carry into blocks with `loggedFrom` on the day's first weights session
 * (or the day's own row), the later weights sessions without them. Session ids, the weights' own
 * blocks and everything else stay as they are; a row read before is read again from scratch.
 */
export function withPartBlocks(week: PublishedWeek, options: ReadOptions = {}): PublishBody {
  const timeZone = options.timeZone ?? defaultTimeZone;
  const cache = new Map<string, DefinitionReading>();
  const readPart = (label: string, lines: string[]): DefinitionReading => {
    const key = `${label}\n${lines.join("\n")}`;
    let found = cache.get(key);
    if (found === undefined) {
      found = readDefinition(label, lines, null, { now: options.now, timeZone, knownExercises: options.knownExercises });
      cache.set(key, found);
    }
    return found;
  };
  const firstOfDay = new Set<string>();
  const sessions: PublishedRow[] = week.sessions.map((row) => {
    const own = row.plan.blocks.filter((b) => b.loggedFrom === undefined);
    const day = `${row.group}\n${row.date}`;
    const isWeights = own.length > 0;
    const carries = !isWeights || !firstOfDay.has(day);
    if (isWeights) firstOfDay.add(day);
    if (!carries) return { group: row.group, sessionID: row.sessionID, date: row.date, plan: { ...row.plan, blocks: own, alsoToday: [] } };
    const start = dateFromDay(row.date, timeZone);
    const seed = daySeed({ date: start, label: row.date, location: null, sessions: new Map() });
    const alsoToday = (row.plan.alsoToday ?? []).map((part) => {
      const { isRun: _isRun, ...rest } = part;
      const reading = readPart(part.label, part.lines ?? []);
      return { reading, wire: reading.family === "run" ? { ...rest, isRun: true as const } : rest };
    });
    const blocksOf = (part: (typeof alsoToday)[number]) =>
      publishedBlocks(part.reading, { label: part.wire.label, seed, lines: part.wire.lines ?? [], loggedFrom: part.wire.label });
    const blocks = [
      ...alsoToday.filter((p) => p.wire.isBeforeLifts).flatMap(blocksOf),
      ...own,
      ...alsoToday.filter((p) => !p.wire.isBeforeLifts).flatMap(blocksOf),
    ];
    return { group: row.group, sessionID: row.sessionID, date: row.date, plan: { ...row.plan, blocks, alsoToday: alsoToday.map((p) => p.wire) } };
  });
  return { columns: week.columns, sessions };
}

export interface PreviewSession {
  /** `YYYY-MM-DD`. */
  date: string;
  /** The grid's label; empty for a day with nothing to lift. */
  label: string;
  sessionID: string;
  reading: WeightsReading;
  alsoToday: ReferenceSessionWire[];
}

/** Each column's week as its athletes' phones will hold it, for the builder's previews. */
export function readDraft(draft: PlanDraft, options: ReadOptions = {}): { group: string; sessions: PreviewSession[] }[] {
  const timeZone = options.timeZone ?? defaultTimeZone;
  return readColumns(toTrainingWeek(draft, { timeZone }), options).map((column) => ({
    group: column.group,
    sessions: column.sessions.map((session) => {
      const plan = planWire(session.plan);
      return {
        date: dayString(session.plan.date ?? session.plan.createdAt, timeZone),
        label: session.label,
        sessionID: session.draftID,
        reading: {
          plan,
          unread: session.reading?.unread.slice() ?? [],
          warnings: session.reading?.warnings.slice() ?? [],
          confidence: session.reading?.confidence ?? 1,
          lines: session.lines.map((line) => ({ ...line, exerciseIds: line.exerciseIds.slice() })),
        },
        alsoToday: plan.alsoToday,
      };
    }),
  }));
}

// MARK: Labels

/** What the app lifts: a label of the Weights family that is not the coach's prep. */
export function isWeightsLabel(label: string): boolean {
  return foldText(label).startsWith("weights") && !isPrepLabel(label);
}

export function labelFamily(label: string): { family: string; number: number } | null {
  if (!looksLikeLabel(label)) return null;
  const key = splitSpaces(labelKey(label));
  const number = Number(key[key.length - 1]!.slice(1));
  const shown = splitSpaces(displayLabel(trimWS(label)));
  const lastPart = shown[shown.length - 1] ?? "";
  let family: string;
  if (lastPart.startsWith("#") || chars(lastPart).every(isNumber)) {
    family = shown.slice(0, -1).join(" ");
  } else {
    const list = chars(lastPart);
    let i = list.length;
    while (i > 0 && isNumber(list[i - 1])) i -= 1;
    family = [...shown.slice(0, -1), list.slice(0, i).join("")].join(" ");
  }
  family = trimWS(family.replace(/[#\s]+$/u, ""));
  return family === "" ? null : { family, number };
}

/** The next free number of a family in this draft: `Weights #4` after `Weights #3`. */
export function nextLabel(draft: PlanDraft, family: string): string {
  const wanted = foldText(family);
  let highest = 0;
  const labels = [
    ...draft.sessions.map((s) => s.label),
    ...draft.days.flatMap((d) => Object.values(d.cells).flat()),
  ];
  for (const label of labels) {
    const found = labelFamily(label);
    if (found !== null && foldText(found.family) === wanted) highest = Math.max(highest, found.number);
  }
  return `${trimWS(family)} #${highest + 1}`;
}

/** The draft's other definitions this one names, in the order it names them. */
export function referencedLabels(definition: PlanDefinition, draft: PlanDraft): string[] {
  const own = labelKey(definition.label);
  const byKey = new Map<string, string>();
  for (const other of draft.sessions) {
    const key = labelKey(other.label);
    if (key !== "" && !byKey.has(key)) byKey.set(key, other.label);
  }
  const out: string[] = [];
  for (const line of definition.lines) {
    const key = labelKey(line);
    const label = byKey.get(key);
    if (key === own || label === undefined || out.includes(label)) continue;
    out.push(label);
  }
  return out;
}

// MARK: A published week back into a draft (DESIGN.md §17.4)

function stripGroupsPhrase(plan: Plan): Plan {
  return {
    ...plan,
    blocks: plan.blocks.map((block) => ({
      ...block,
      exercises: block.exercises.map((e) => {
        const phrase = groupsPhrase(e);
        if (phrase === null || e.note === null || !e.note.startsWith(phrase)) return e;
        const rest = chars(e.note).slice(count(phrase));
        let i = 0;
        while (i < rest.length && (rest[i] === " " || rest[i] === "·")) i += 1;
        const left = rest.slice(i).join("");
        return { ...e, note: left === "" ? null : left };
      }),
    })),
  };
}

function utcDay(monday: string, offset: number): string {
  const [y, m, d] = monday.split("-").map(Number) as [number, number, number];
  const ms = Date.UTC(y, m - 1, d + offset);
  return new Date(ms).toISOString().slice(0, 10);
}

/** The label a session id was made from, when it is one of the usual spellings. */
function recoverLabel(sessionID: string, date: string, timeZone: string): string | null {
  const start = dateFromDay(date, timeZone);
  if (start === null) return null;
  const day: TrainingDay = { date: start, label: "", location: null, sessions: new Map() };
  for (let n = 1; n <= 99; n += 1) {
    for (const candidate of [`Weights #${n}`, `Weights ${n}`, `Weights${n}`]) {
      if (identifierOn(candidate, day) === sessionID) return candidate;
    }
  }
  return identifierOn("Weights", day) === sessionID ? "Weights" : null;
}

export function draftFromPublished(week: PublishedWeek, monday: string, options: { timeZone?: string } = {}): PlanDraft {
  const timeZone = options.timeZone ?? defaultTimeZone;
  const start = mondayOf(monday) ?? monday;
  const names = week.columns.length > 0 ? week.columns.slice() : week.sessions.length > 0 ? ["Everyone"] : [];
  const columns: PlanColumn[] = names.map((name, index) => ({ id: `c${index + 1}`, name }));
  const everyoneColumn = columns.find((c) => isEveryone(c.name)) ?? null;
  const days = Array.from({ length: 7 }, (_, offset) => utcDay(start, offset));

  // §61.4: a part's blocks are the part's, read again from its lines; only the weights' own are rendered.
  const rows = week.sessions.map((row) => ({
    ...row,
    plan: stripGroupsPhrase(planFromWire({ ...row.plan, blocks: row.plan.blocks.filter((b) => b.loggedFrom === undefined) })),
  }));

  const labels = new Map<string, string>();
  const byText = new Map<string, string>();
  const used = new Set<string>();
  for (const row of rows) for (const other of row.plan.alsoToday) used.add(labelKey(other.label));
  const definitions: PlanDefinition[] = [];
  const definedText = new Map<string, string>();
  const ordered = rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => (a.row.date < b.row.date ? -1 : a.row.date > b.row.date ? 1 : a.index - b.index));

  for (const { row } of ordered) {
    const plan = row.plan;
    if (plan.blocks.length === 0 || labels.has(row.sessionID)) continue;
    const lines = renderPlan(plan.blocks).split("\n");
    const text = lines.join("\n");
    const recovered = recoverLabel(row.sessionID, row.date, timeZone);
    const recoveredText = recovered === null ? undefined : definedText.get(labelKey(recovered));
    let label: string;
    if (recovered !== null && (recoveredText === undefined || recoveredText === text)) {
      label = recovered;
    } else {
      label = byText.get(text) ?? "";
      if (label === "") {
        let n = 1;
        while (used.has(labelKey(`Weights #${n}`)) || definedText.has(labelKey(`Weights #${n}`))) n += 1;
        label = `Weights #${n}`;
      }
    }
    const key = labelKey(label);
    if (!definedText.has(key)) {
      definedText.set(key, text);
      definitions.push({ label, lines });
    }
    if (!byText.has(text)) byText.set(text, label);
    labels.set(row.sessionID, label);
  }
  for (const { row } of ordered) {
    for (const other of row.plan.alsoToday) {
      const key = labelKey(other.label);
      if (other.lines.length === 0 || definedText.has(key)) continue;
      definedText.set(key, other.lines.join("\n"));
      definitions.push({ label: other.label, lines: other.lines.slice() });
    }
  }

  const cellsFor = (group: string, date: string): string[] => {
    const dayRows = ordered.filter(({ row }) => row.group === group && row.date === date).map(({ row }) => row);
    const weights = dayRows.filter((row) => row.plan.blocks.length > 0);
    if (weights.length === 0) {
      const out: string[] = [];
      for (const row of dayRows) for (const other of row.plan.alsoToday) if (!out.includes(other.label)) out.push(other.label);
      return out;
    }
    const slots: string[][] = Array.from({ length: weights.length + 1 }, () => []);
    const placed = new Set<string>();
    for (const row of weights) {
      for (const other of row.plan.alsoToday) {
        const key = labelKey(other.label);
        if (placed.has(key)) continue;
        placed.add(key);
        const carrying = weights.filter((w) => w.plan.alsoToday.some((o) => labelKey(o.label) === key));
        // §61.4: a part rides on the day's first weights session alone, so it is before them all or after them all.
        const after =
          carrying.length < weights.length
            ? carrying[0]!.plan.alsoToday.find((o) => labelKey(o.label) === key)!.isBeforeLifts
              ? weights.indexOf(carrying[0]!)
              : weights.length
            : weights.filter((w) => {
                const seen = w.plan.alsoToday.find((o) => labelKey(o.label) === key);
                return seen === undefined || !seen.isBeforeLifts;
              }).length;
        slots[after]!.push(other.label);
      }
    }
    const out: string[] = [...slots[0]!];
    weights.forEach((row, index) => {
      out.push(labels.get(row.sessionID)!);
      out.push(...slots[index + 1]!);
    });
    return out;
  };

  const planDays: PlanDay[] = days.map((date) => {
    const cells: Record<string, string[]> = {};
    const shared = cellsFor("", date);
    const sharedKeys = new Set(shared.map(labelKey));
    if (everyoneColumn !== null && shared.length > 0) cells[everyoneColumn.id] = shared;
    for (const column of columns) {
      if (isEveryone(column.name)) continue;
      const own = cellsFor(column.name, date).filter((entry) => !sharedKeys.has(labelKey(entry)));
      if (own.length > 0) cells[column.id] = own;
    }
    return { date, location: "", cells };
  });

  return { week: start, title: "", columns, days: planDays, sessions: definitions, notes: [] };
}

